import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/apiConfig';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Link } from 'react-router-dom';
import { useAuth } from '../components/router/authContext';

const EmailVerificationPage = () => {
  const [verificationStatus, setVerificationStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  // Animation hook for form entry
  const [formRef, isFormVisible] = useScrollAnimation({ threshold: 0.3 });
  
  // Add debugging logs
  console.log('EmailVerificationPage rendered');
  console.log('Current URL:', window.location.href);
  console.log('Location object:', location);
  console.log('Pathname:', location.pathname);

  useEffect(() => {
    console.log('Verification useEffect running');
    
    const verifyEmail = async () => {
      console.log('verifyEmail function called');
      
      // Extract token from URL path
      // The pathname will be something like "/verify-email/TOKEN"
      const pathParts = location.pathname.split('/');
      const token = pathParts[pathParts.length - 1]; // Get the last part of the path
      
      console.log('Token from URL path:', token);
      
      if (!token || token === 'verify-email') {
        console.log('No valid token found in URL path');
        setVerificationStatus('error');
        setErrorMessage('Verification token is missing');
        return;
      }

      try {
        console.log('Making API request to verify email with token:', token);
        // Call the verification API - IMPORTANT: Don't auto-login here
        const response = await axios.post(API_ENDPOINTS.verifyEmail, { token });
        console.log('API response:', response.data);
        
        // Handle success - just show verification success, don't login
        logout();
        setVerificationStatus('success');
      } catch (error) {
        console.error('Email verification API error:', error);
        // Handle error
        setVerificationStatus('error');
        setErrorMessage(
          error.response?.data?.error || 
          'Failed to verify email. Please try again or contact support.'
        );
      }
    };

    // Call the verification function
    verifyEmail();
  }, [location.pathname]);

  // Log when verification status changes
  useEffect(() => {
    console.log('Verification status changed to:', verificationStatus);
  }, [verificationStatus]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col relative">
      {/* Logo in top left corner */}
      <div className={`absolute top-6 left-6 z-10 transition-all duration-1000 ease-out transform ${
        isFormVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}>
        <Link to="/">
          <img src="/img/logo.png" alt="AmpTrans Logo" className="w-auto h-12" />
        </Link>
      </div>
      
      {/* Main content container - centered */}
      <div className="flex-1 flex items-center justify-center px-4 py-20">
        <div
          ref={formRef}
          className={`w-full max-w-2xl transition-all duration-1000 ease-out transform ${
            isFormVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="w-full flex justify-center items-center p-8">
            <div className="w-full bg-white rounded-lg p-8 shadow-lg">
              
              {verificationStatus === 'verifying' && (
                <div className="text-center">
                  <div className="mb-6">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2 font-nunito">Verifying Email</h2>
                    <p className="text-sm sm:text-base text-gray-600 font-nunito">Please wait while we verify your email address...</p>
                  </div>
                </div>
              )}
              
              {verificationStatus === 'success' && (
                <div className="text-center">
                  <div className="mb-6">
                    <div className="text-6xl mb-4 text-green-500">✓</div>
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2 font-nunito">Email Verified Successfully!</h2>
                    <p className="text-sm sm:text-base text-gray-600 font-nunito mb-6">
                      Your email has been verified. You can now log in to your account.
                    </p>
                    
                    {/* Login Button */}
                    <div className="flex justify-center">
                      <Link 
                        to="/login"
                        className="font-nunito uppercase text-white bg-gradient-to-r from-primary to-primary-light py-3 px-8 tracking-wider text-sm font-medium rounded-lg border-0 transition-all duration-500 hover:shadow-lg"
                      >
                        Go to Login
                      </Link>
                    </div>
                  </div>
                </div>
              )}
              
              {verificationStatus === 'error' && (
                <div className="text-center">
                  <div className="mb-6">
                    <div className="text-6xl mb-4 text-red-500">✗</div>
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2 font-nunito">Verification Failed</h2>
                    <p className="text-sm sm:text-base text-red-600 font-nunito mb-2">{errorMessage}</p>
                    <p className="text-sm sm:text-base text-gray-600 font-nunito mb-6">
                      Please try again or contact support if the problem persists.
                    </p>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Link 
                        to="/login"
                        className="font-nunito uppercase text-white bg-gradient-to-r from-primary to-primary-light py-3 px-6 tracking-wider text-sm font-medium rounded-lg border-0 transition-all duration-500 hover:shadow-lg text-center"
                      >
                        Back to Login
                      </Link>
                      <button 
                        onClick={() => window.location.reload()}
                        className="font-nunito uppercase text-primary bg-transparent border-2 border-primary py-3 px-6 tracking-wider text-sm font-medium rounded-lg transition-all duration-500 hover:bg-primary hover:text-white"
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage; 